import { useCallback, useEffect, useState } from 'react';

import { useBranchContext } from 'state/branch';
import { useMParticleContext } from 'state/mParticle';
import * as Braze from 'utils/braze';
import LocalStorage, { StorageKeys } from 'utils/local-storage';

import { useExternalSdk } from './use-external-sdk';
import { addEventListener, handleConsents, isBranchAvailable, removeEventListeners } from './utils';

export const ExternalBanner = () => {
  const { init: initMParticle } = useMParticleContext();
  const { initBranch } = useBranchContext();
  const { loaded: otSdkLoaded } = useExternalSdk();

  const [librariesLoaded, setLibrariesLoaded] = useState(false);
  const [consentGroupsSetInLocalStorage, setConsentGroupsSetInLocalStorage] = useState(false);
  const branchAvailable = isBranchAvailable();

  // Function to initialize 3rd party services
  const initServicesWithCookies = useCallback(() => {
    if (!window.mParticle) {
      initMParticle();
    }
    if (!window.branch && branchAvailable) {
      initBranch();
    }
  }, [initBranch, initMParticle, branchAvailable]);

  const getConsentCookies = () => window?.UC_UI?.getServicesBaseInfo() || [];

  const getNonEssentialConsents = useCallback(() => {
    const nonEssentialConsents = getConsentCookies()
      .filter((data: { isEssential: any }) => !data.isEssential)
      .map((consent: { name: string; consent: { status: any } }) => {
        return {
          name: consent?.name.toLowerCase().split(' ')[0],
          enable: consent?.consent.status,
        };
      });
    return nonEssentialConsents;
  }, []);

  const setCookieConsents = useCallback(() => {
    const nonEssentialConsents = getNonEssentialConsents();
    handleConsents(nonEssentialConsents);
    LocalStorage.setItem(StorageKeys.USER_CENTRICS_COOKIE_CONSENT_GROUPS, getConsentCookies());
  }, [getNonEssentialConsents]);

  const areLibrariesInitialized = useCallback(() => {
    const branchValidation = !branchAvailable || !!window.branch;
    const areServicesInitialized = !!window.mParticle && branchValidation && !!Braze.getSdk();
    return areServicesInitialized;
  }, [branchAvailable]);

  // loads 3rd party services once OneTrust sdk is loaded
  useEffect(() => {
    if (!librariesLoaded) {
      initServicesWithCookies();
    }
  }, [initServicesWithCookies, librariesLoaded]);

  // Sets a interval to check if all 3rd party services are initialized
  useEffect(() => {
    const id = setInterval(() => {
      if (areLibrariesInitialized()) {
        setLibrariesLoaded(true);
      }
    }, 1000);
    return () => {
      clearInterval(id);
    };
  }, [areLibrariesInitialized]);

  // Registers external banner consent change handler
  useEffect(() => {
    const id = setInterval(() => {
      if (window?.UC_UI) {
        if (!consentGroupsSetInLocalStorage) {
          LocalStorage.setItem(
            StorageKeys.USER_CENTRICS_COOKIE_CONSENT_GROUPS,
            getConsentCookies()
          );
          setConsentGroupsSetInLocalStorage(true);
        }
        addEventListener(setCookieConsents);
      }
    }, 1000);
    if (consentGroupsSetInLocalStorage) {
      clearInterval(id);
    }
    return () => {
      removeEventListeners(setCookieConsents);
      clearInterval(id);
    };
  }, [otSdkLoaded, consentGroupsSetInLocalStorage, setCookieConsents]);

  // Switches off tracking for each service if data tracking is opted out
  useEffect(() => {
    // if consent state is opted out for respective service, data tracking is opted out
    if (librariesLoaded) {
      const nonEssentialConsents = getNonEssentialConsents();
      handleConsents(nonEssentialConsents);
    }
  }, [getNonEssentialConsents, librariesLoaded]);

  return null;
};
